'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { useRadio } from '@/lib/GlobalRadioContext';
import {
  getActiveNews, markDeferred, updateNewsStatus,
  getNotificationText, getNotificationSubText, getNotificationPrimaryLabel, getNotificationHref,
  contactsById,
  type SocialNewsItem, type NewsType
} from '@/lib/socialNews';

// ── Teal palette constants ──────────────────────────────────────────────────
const TEAL = {
  bg: '#E8F4F1',
  border: '#69A999',
  textMain: '#173E36',
  btnPrimary: '#247B68',
  badge: '#176F5B',
  btnSecondaryBorder: '#69A999',
};

function triggerHaptic() {
  try { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(20); } catch { /* ignore */ }
}

// ── Type icon ────────────────────────────────────────────────────────────────
function TypeIcon({ type, color }: { type: NewsType; color: string }) {
  if (type === 'missed-call' || type === 'callback-request') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 4.69 13" />
        <path d="M22 2L11 13" /><path d="M22 2l-5 5" /><path d="M17 2l5 5" />
      </svg>
    );
  }
  if (type === 'photo') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    );
  }
  if (type === 'event') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

// ── Social News Tile ────────────────────────────────────────────────────────
function SocialNewsTile() {
  const router = useRouter();
  const [current, setCurrent] = useState<SocialNewsItem | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [actionState, setActionState] = useState<'idle' | 'loading'>('idle');
  const [deferPressed, setDeferPressed] = useState(false);
  const [seeAllPressed, setSeeAllPressed] = useState(false);

  const refresh = () => {
    const active = getActiveNews();
    setActiveCount(active.length);
    setCurrent(active[0] ?? null);
  };

  useEffect(() => { refresh(); }, []);

  const handleDefer = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!current || deferPressed) return;
    triggerHaptic();
    setDeferPressed(true);
    setTimeout(() => {
      markDeferred(current.id);
      refresh();
      setDeferPressed(false);
    }, 180);
  };

  const handleSeeAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (seeAllPressed) return;
    triggerHaptic();
    setSeeAllPressed(true);
    setTimeout(() => router.push('/actualites'), 180);
  };

  const openNotification = useCallback((notification: SocialNewsItem) => {
    if (actionState !== 'idle') return;
    triggerHaptic();
    setActionState('loading');
    setTimeout(() => {
      if (notification.status === 'unread') updateNewsStatus(notification.id, 'seen');
      router.push(getNotificationHref(notification));
    }, 200);
  }, [actionState, router]);

  const handlePrimaryAction = () => {
    if (!current) return;
    openNotification(current);
  };

  const tileBase: React.CSSProperties = {
    background: TEAL.bg,
    border: `2px solid ${TEAL.border}`,
    borderRadius: '16px',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    minHeight: 0,
    overflow: 'hidden',
  };

  if (!current) {
    return (
      <div style={tileBase}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={TEAL.btnPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: TEAL.textMain }}>Dernières nouvelles</div>
            <div style={{ fontSize: '15px', color: '#5A7A74' }}>de mes proches</div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '18px', color: '#5A7A74', fontWeight: 600 }}>Tout est à jour ✓</span>
        </div>
        <button
          onClick={handleSeeAll}
          aria-label="Voir toutes les actualités"
          disabled={seeAllPressed}
          className="interactive-btn"
          style={{
            marginTop: 'auto',
            background: seeAllPressed ? '#E8F4F1' : 'white',
            border: `1.5px solid ${TEAL.btnSecondaryBorder}`,
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '17px',
            fontWeight: 700,
            color: TEAL.btnPrimary,
            cursor: seeAllPressed ? 'not-allowed' : 'pointer',
            minHeight: '52px',
          }}
        >
          {seeAllPressed ? 'Ouverture…' : 'Voir tout'}
        </button>
      </div>
    );
  }

  const contact = contactsById[current.contactId];
  const displayText = getNotificationText(current);
  const displaySubText = getNotificationSubText(current);
  const primaryLabel = getNotificationPrimaryLabel(current);

  return (
    <div
      style={tileBase}
      onClick={handlePrimaryAction}
      role="button"
      tabIndex={0}
      aria-label={`${displayText} — ${primaryLabel}`}
      onKeyDown={e => e.key === 'Enter' && handlePrimaryAction()}
      className="interactive-card"
    >
      {/* Header: title + badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: TEAL.textMain, lineHeight: 1.2, flex: 1, minWidth: 0 }}>
          Dernières nouvelles de mes proches
        </div>
        {activeCount > 0 && (
          <div style={{
            background: TEAL.badge, color: 'white', borderRadius: '20px',
            padding: '2px 10px', fontSize: '13px', fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {activeCount} {activeCount === 1 ? 'nouvelle' : 'nouvelles'}
          </div>
        )}
      </div>

      {/* Contact row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {contact && (
          <div style={{ width: '52px', height: '52px', borderRadius: '50%', overflow: 'hidden', border: `2.5px solid ${TEAL.border}`, flexShrink: 0 }}>
            <AppImage src={contact.photo} alt={contact.photoAlt} width={52} height={52} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {displaySubText && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
              <TypeIcon type={current.type} color={current.iconColor} />
              <span style={{ fontSize: '14px', fontWeight: 700, color: current.iconColor }}>{displaySubText}</span>
            </div>
          )}
          <div style={{ fontSize: '17px', fontWeight: 700, color: TEAL.textMain, lineHeight: 1.25 }}>{displayText}</div>
        </div>
      </div>

      {/* Primary action */}
      <button
        onClick={e => { e.stopPropagation(); handlePrimaryAction(); }}
        aria-label={actionState === 'loading' ? 'Ouverture…' : primaryLabel}
        aria-busy={actionState === 'loading'}
        disabled={actionState !== 'idle'}
        className="interactive-btn"
        style={{
          marginTop: 'auto',
          background: actionState === 'loading' ? '#176F5B' : TEAL.btnPrimary,
          border: 'none',
          borderRadius: '12px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          minHeight: '52px',
          cursor: actionState !== 'idle' ? 'not-allowed' : 'pointer',
        }}
      >
        {actionState === 'loading' && (
          <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.8)', borderTopColor: 'transparent', animation: 'sab-spin 0.7s linear infinite', flexShrink: 0 }} aria-hidden="true" />
        )}
        <span style={{ fontSize: '17px', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
          {actionState === 'loading' ? 'Ouverture…' : primaryLabel}
        </span>
      </button>

      {/* Secondary row */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleDefer}
          aria-label="Reporter à plus tard"
          disabled={deferPressed}
          className="interactive-btn"
          style={{
            flex: 1, background: deferPressed ? '#FFF3E0' : 'white',
            border: `1.5px solid ${deferPressed ? '#F5A623' : TEAL.btnSecondaryBorder}`,
            borderRadius: '10px', padding: '8px 10px', fontSize: '15px',
            fontWeight: 700, color: TEAL.btnPrimary, cursor: deferPressed ? 'not-allowed' : 'pointer', minHeight: '44px', whiteSpace: 'nowrap',
          }}
        >
          Plus tard
        </button>
        <button
          onClick={handleSeeAll}
          aria-label="Voir toutes les nouvelles"
          disabled={seeAllPressed}
          className="interactive-btn"
          style={{
            flex: 1, background: seeAllPressed ? '#E8F4F1' : 'white',
            border: `1.5px solid ${TEAL.btnSecondaryBorder}`,
            borderRadius: '10px', padding: '8px 10px', fontSize: '15px',
            fontWeight: 700, color: TEAL.textMain, cursor: seeAllPressed ? 'not-allowed' : 'pointer', minHeight: '44px', whiteSpace: 'nowrap',
          }}
        >
          {seeAllPressed ? 'Ouverture…' : 'Voir tout'}
        </button>
      </div>
    </div>
  );
}

// ── Radio Tile ──────────────────────────────────────────────────────────────
function RadioTile() {
  const router = useRouter();
  const { playState, selectedStation, selectAndPlay, pause, resume, stop } = useRadio();
  const [listenPressed, setListenPressed] = useState(false);
  const [pausePressed, setPausePressed] = useState(false);
  const [resumePressed, setResumePressed] = useState(false);
  const [stopPressed, setStopPressed] = useState(false);

  const RADIO_BG = '#FFF8EC';
  const RADIO_BORDER = 'rgba(245,166,35,0.3)';
  const RADIO_ACCENT = '#F5A623';
  const RADIO_ACCENT_LIGHT = '#FEF3C7';
  const RADIO_STOP_BG = '#FEE2E2';
  const RADIO_STOP_COLOR = '#B91C1C';

  const stationName = selectedStation?.name ?? 'RCI';

  const handleListen = useCallback(() => {
    if (listenPressed) return;
    triggerHaptic();
    setListenPressed(true);
    setTimeout(() => {
      selectAndPlay('rci');
      router.push('/ma-radio');
    }, 180);
  }, [listenPressed, selectAndPlay, router]);

  const handlePause = useCallback(() => {
    if (pausePressed) return;
    triggerHaptic();
    setPausePressed(true);
    setTimeout(() => { pause(); setPausePressed(false); }, 150);
  }, [pausePressed, pause]);

  const handleResume = useCallback(() => {
    if (resumePressed) return;
    triggerHaptic();
    setResumePressed(true);
    setTimeout(() => { resume(); setResumePressed(false); }, 150);
  }, [resumePressed, resume]);

  const handleStop = useCallback(() => {
    if (stopPressed) return;
    triggerHaptic();
    setStopPressed(true);
    setTimeout(() => { stop(); setStopPressed(false); }, 150);
  }, [stopPressed, stop]);

  const HeadphoneIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={RADIO_ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" />
      <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  );

  const AudioBars = () => (
    <div style={{ display: 'flex', gap: '3px', alignItems: 'center', height: '24px', flexShrink: 0 }}>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{ width: '4px', background: RADIO_ACCENT, borderRadius: '2px', animation: 'audioBars 0.7s ease-in-out infinite', animationDelay: `${i * 0.12}s`, height: '100%' }} />
      ))}
      <style>{`@keyframes audioBars { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }`}</style>
    </div>
  );

  const tileBase: React.CSSProperties = {
    background: RADIO_BG, border: `2px solid ${RADIO_BORDER}`, borderRadius: '16px',
    padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', minHeight: 0, overflow: 'hidden',
  };

  if (playState === 'idle' || playState === 'error') {
    return (
      <div style={tileBase}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <HeadphoneIcon />
          <div>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#1A2744', lineHeight: 1.1 }}>RCI</div>
            <div style={{ fontSize: '15px', color: '#6B7280', fontWeight: 500 }}>Votre radio favorite</div>
          </div>
        </div>
        <button
          onClick={handleListen}
          aria-label={listenPressed ? 'Chargement de la radio…' : 'Écouter la radio RCI'}
          aria-busy={listenPressed}
          disabled={listenPressed}
          className="interactive-btn"
          style={{
            marginTop: 'auto', background: listenPressed ? '#D97706' : RADIO_ACCENT, border: 'none',
            borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: '8px', minHeight: '52px', cursor: listenPressed ? 'not-allowed' : 'pointer',
          }}
        >
          {listenPressed ? (
            <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.8)', borderTopColor: 'transparent', animation: 'sab-spin 0.7s linear infinite' }} aria-hidden="true" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )}
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>
            {listenPressed ? 'Chargement…' : 'Écouter la radio'}
          </span>
        </button>
      </div>
    );
  }

  if (playState === 'loading') {
    return (
      <div style={tileBase}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: `3px solid ${RADIO_ACCENT}`, borderTopColor: 'transparent', animation: 'sab-spin 0.8s linear infinite', flexShrink: 0 }} />
          <div style={{ fontSize: '19px', fontWeight: 700, color: '#1A2744', lineHeight: 1.3 }}>Chargement de {stationName}…</div>
        </div>
        <button onClick={handleStop} aria-label="Annuler le chargement" className="interactive-btn"
          style={{ marginTop: 'auto', background: RADIO_STOP_BG, border: `1.5px solid rgba(185,28,28,0.3)`, borderRadius: '12px', padding: '12px 16px', fontSize: '17px', fontWeight: 700, color: RADIO_STOP_COLOR, cursor: 'pointer', minHeight: '52px' }}>
          Annuler
        </button>
      </div>
    );
  }

  if (playState === 'playing' || playState === 'fallback') {
    return (
      <div style={tileBase}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <AudioBars />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#1A2744', lineHeight: 1.1 }}>{stationName}</div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: RADIO_ACCENT }}>En cours de lecture</div>
            {playState === 'fallback' && <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '2px' }}>Version de démonstration</div>}
          </div>
        </div>
        <button onClick={handlePause} aria-label="Mettre la radio en pause" disabled={pausePressed} className="interactive-btn"
          style={{ marginTop: 'auto', background: pausePressed ? RADIO_ACCENT : RADIO_ACCENT_LIGHT, border: `2px solid ${RADIO_ACCENT}`, borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '52px', cursor: 'pointer' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill={pausePressed ? 'white' : RADIO_ACCENT} stroke={pausePressed ? 'white' : RADIO_ACCENT} strokeWidth="1">
            <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
          </svg>
          <span style={{ fontSize: '18px', fontWeight: 700, color: pausePressed ? 'white' : RADIO_ACCENT }}>Mettre en pause</span>
        </button>
        <button onClick={handleStop} aria-label="Arrêter la radio" disabled={stopPressed} className="interactive-btn"
          style={{ background: stopPressed ? '#FCA5A5' : RADIO_STOP_BG, border: `1.5px solid rgba(185,28,28,0.3)`, borderRadius: '12px', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '44px', cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill={RADIO_STOP_COLOR} stroke="none"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
          <span style={{ fontSize: '16px', fontWeight: 700, color: RADIO_STOP_COLOR }}>Arrêter</span>
        </button>
      </div>
    );
  }

  if (playState === 'paused') {
    return (
      <div style={tileBase}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill={RADIO_ACCENT} stroke={RADIO_ACCENT} strokeWidth="1" style={{ flexShrink: 0 }}>
            <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
          </svg>
          <div style={{ fontSize: '19px', fontWeight: 800, color: '#1A2744', lineHeight: 1.2 }}>{stationName} est en pause</div>
        </div>
        <button onClick={handleResume} aria-label="Reprendre la radio" disabled={resumePressed} className="interactive-btn"
          style={{ marginTop: 'auto', background: resumePressed ? '#D97706' : RADIO_ACCENT, border: 'none', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '52px', cursor: 'pointer' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>Reprendre</span>
        </button>
        <button onClick={handleStop} aria-label="Arrêter la radio" disabled={stopPressed} className="interactive-btn"
          style={{ background: stopPressed ? '#FCA5A5' : RADIO_STOP_BG, border: `1.5px solid rgba(185,28,28,0.3)`, borderRadius: '12px', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '44px', cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill={RADIO_STOP_COLOR} stroke="none"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
          <span style={{ fontSize: '16px', fontWeight: 700, color: RADIO_STOP_COLOR }}>Arrêter</span>
        </button>
      </div>
    );
  }

  return null;
}

// ── Agenda Tile ─────────────────────────────────────────────────────────────
function AgendaTile() {
  const router = useRouter();
  const [pressed, setPressed] = useState(false);

  const handleClick = useCallback(() => {
    if (pressed) return;
    triggerHaptic();
    setPressed(true);
    setTimeout(() => router.push('/ma-journee'), 180);
  }, [pressed, router]);

  return (
    <button
      onClick={handleClick}
      aria-label={pressed ? 'Ouverture de l\'agenda…' : 'Lire ma journée — Visite de Marie à 15h'}
      aria-busy={pressed}
      disabled={pressed}
      className="interactive-btn"
      style={{
        background: '#EFF6FF', border: '2px solid rgba(59,130,246,0.3)', borderRadius: '16px',
        padding: '14px', cursor: pressed ? 'not-allowed' : 'pointer', display: 'flex',
        flexDirection: 'column', gap: '10px', textAlign: 'left', minHeight: 0, overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '32px', fontWeight: 800, color: '#3B82F6', lineHeight: 1 }}>15h00</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#1A2744', marginTop: '2px' }}>Visite de Marie</div>
        </div>
      </div>
      <div style={{
        marginTop: 'auto', background: pressed ? '#2563EB' : '#3B82F6', borderRadius: '12px',
        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '52px',
      }}>
        {pressed ? (
          <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.8)', borderTopColor: 'transparent', animation: 'sab-spin 0.7s linear infinite' }} aria-hidden="true" />
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        )}
        <span style={{ fontSize: '18px', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
          {pressed ? 'Ouverture de l\'agenda…' : 'Lire ma journée'}
        </span>
      </div>
    </button>
  );
}

// ── Activity Tile ───────────────────────────────────────────────────────────
function ActivityTile() {
  const router = useRouter();
  const [pressed, setPressed] = useState(false);

  const handleClick = useCallback(() => {
    if (pressed) return;
    triggerHaptic();
    setPressed(true);
    setTimeout(() => router.push('/quiz-martinique'), 180);
  }, [pressed, router]);

  return (
    <button
      onClick={handleClick}
      aria-label={pressed ? 'Ouverture du quiz…' : 'Continuer le quiz Martinique'}
      aria-busy={pressed}
      disabled={pressed}
      className="interactive-btn"
      style={{
        background: '#F5F3FF', border: '2px solid rgba(139,92,246,0.3)', borderRadius: '16px',
        padding: '14px', cursor: pressed ? 'not-allowed' : 'pointer', display: 'flex',
        flexDirection: 'column', gap: '10px', textAlign: 'left', minHeight: 0, overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="3" />
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#1A2744', lineHeight: 1.1 }}>Quiz Martinique</div>
        </div>
      </div>
      <div style={{ marginTop: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '16px', fontWeight: 600, color: '#6B7280' }}>Progression</span>
          <span style={{ fontSize: '16px', fontWeight: 700, color: '#8B5CF6' }}>65%</span>
        </div>
        <div style={{ height: '8px', background: 'rgba(139,92,246,0.15)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ width: '65%', height: '100%', background: '#8B5CF6', borderRadius: '4px' }} />
        </div>
      </div>
      <div style={{
        marginTop: 'auto', background: pressed ? '#6D28D9' : '#8B5CF6', borderRadius: '12px',
        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '52px',
      }}>
        {pressed ? (
          <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.8)', borderTopColor: 'transparent', animation: 'sab-spin 0.7s linear infinite' }} aria-hidden="true" />
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polygon points="10 8 16 12 10 16 10 8" fill="white" stroke="none" />
          </svg>
        )}
        <span style={{ fontSize: '18px', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
          {pressed ? 'Ouverture…' : 'Continuer le quiz'}
        </span>
      </div>
    </button>
  );
}

// ── Main Grid ───────────────────────────────────────────────────────────────
export default function QuickTilesGrid() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '12px', height: '100%', minHeight: 0 }}>
      <SocialNewsTile />
      <RadioTile />
      <AgendaTile />
      <ActivityTile />
      <style>{`@keyframes sab-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}